import { create } from 'zustand';
import { authService } from 'src/services/auth';
import type { MeResponse } from 'src/services/auth';

interface AuthState {
  token: string | null;
  user: MeResponse | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setToken: (token: string | null) => void;
  setUser: (user: MeResponse | null) => void;
  fetchMeOnce: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  token: typeof window !== 'undefined' ? localStorage.getItem('token') : null,
  user: null,
  isAuthenticated: typeof window !== 'undefined' ? !!localStorage.getItem('token') : false,
  loading: false,
  error: null,
  setToken: (token) => {
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
    }
    set({ token, isAuthenticated: !!token });
  },
  setUser: (user) => set({ user }),
  fetchMeOnce: async () => {
    const hasToken = typeof window !== 'undefined' ? !!localStorage.getItem('token') : false;
    if (!hasToken) return;
    // Only fetch if user isn't already loaded
    const current = (useAuthStore.getState && useAuthStore.getState().user) || null;
    if (current) return;
    try {
      const me = await authService.me();
      set({ user: me, isAuthenticated: true });
    } catch (error) {
      // If token invalid, logout
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
      }
      set({ token: null, user: null, isAuthenticated: false });
    }
  },
  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const result = await authService.login({ email, password });
      const accessToken = result?.accessToken || result?.token;
      if (accessToken) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('token', accessToken);
        }
        set({ token: accessToken, isAuthenticated: true });
        // After successful login, fetch user profile
        try {
          const me = await authService.me();
          set({ user: me });
        } catch {
          // ignore profile fetch errors here
        }
      }
      // Do not set user from login response unless it strictly conforms
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message || 'Login failed';
      set({ error: message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  logout: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
    }
    set({ token: null, user: null, isAuthenticated: false });
  },
}));


